import {Button} from '@blueprintjs/core';
import {useObserver} from 'mobx-react-lite';
import * as queryString from 'query-string';
import React, {useEffect, useRef, useState} from 'react';
import ReactMarkdown from 'react-markdown';
import {useHistory, useLocation} from 'react-router-dom';
import rehypeSlug from 'rehype-slug';
import remarkGfm from 'remark-gfm';
import {LoadingIndicator} from '../../components/LoadingIndicator';
import {AppDetailStore} from '../../stores/AppDetailStore';
import styles from './AppOverview.module.sass';
import {
  assetsRoot,
  docParam,
  docUrl,
  OVERVIEW_DOC,
  parseDocParam,
  resolveImageUri,
  resolveLink,
} from './overviewLinks';

interface Props {
  detailStore: AppDetailStore;
}

export const AppOverview = ({detailStore}: Props) => {
  const [content, setContent] = useState<{url: string; text: string} | undefined>();
  const containerRef = useRef<HTMLDivElement>(null);
  const shownUrl = useRef<string>();
  const history = useHistory();
  const location = useLocation();
  const detail = useObserver(() => detailStore.detail);
  const root = detail ? assetsRoot(detail.overview_url) : '';
  const {doc, id} = parseDocParam(root, queryString.parse(location.search).doc as string | undefined);
  const url = root ? docUrl(root, doc) : '';

  useEffect(() => {
    if (!url) {
      return;
    }
    let cancelled = false;
    fetch(url)
      .then(async (result) => {
        if (!result.ok) {
          throw new Error();
        }
        return await result.text() || '';
      })
      .catch(() => 'There was a problem loading this information.')
      .then((text) => {
        if (!cancelled) {
          setContent({url, text});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const scrollTo = (headingId: string) => {
    const container = containerRef.current;
    Array.from(container?.querySelectorAll('[id]') ?? []).find((el) => el.id === headingId)?.scrollIntoView();
  };

  const loaded = content?.url === url;

  useEffect(() => {
    if (!loaded) {
      return;
    }
    if (id) {
      scrollTo(id);
    } else if (shownUrl.current && shownUrl.current !== url) {
      containerRef.current?.scrollIntoView();
    }
    shownUrl.current = url;
  }, [loaded, url, id]);

  if (!detail || detailStore.loading || !loaded) {
    return (
      <LoadingIndicator />
    );
  }

  const navigate = (nextDoc: string, nextId?: string) => {
    const query = queryString.parse(location.search);
    delete query.doc;
    const param = docParam(nextDoc, nextId);
    history.push({...location, search: queryString.stringify(param ? {...query, doc: param} : query)});
  };

  const renderLink = ({node: _node, href, children, ...props}: any) => {
    const link = resolveLink(href, root, doc);
    switch (link.kind) {
      case 'anchor':
        return (
          <a {...props} href={`#${encodeURIComponent(link.id)}`} onClick={(e) => {
            e.preventDefault();
            scrollTo(link.id);
          }}>{children}</a>
        );
      case 'doc':
        return (
          <a {...props} href={link.href} onClick={(e) => {
            e.preventDefault();
            navigate(link.doc, link.id);
          }}>{children}</a>
        );
      default:
        return <a {...props} href={link.href} target="_blank" rel="noopener noreferrer">{children}</a>;
    }
  };

  return (
    <div ref={containerRef}>
      {doc !== OVERVIEW_DOC && (
        <Button
          className={styles.back}
          minimal
          icon="arrow-left"
          text="Back to overview"
          onClick={() => navigate(OVERVIEW_DOC)}
        />
      )}
      <ReactMarkdown
        key={url}
        className={styles.markdown}
        children={content.text}
        transformImageUri={(src) => resolveImageUri(src, root, doc)}
        components={{a: renderLink}}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug]}
      />
    </div>
  );
};
